<template>
  <section class="gift-item subWrapper">
    <h1 class="title">{{ $t('gift-shop.key002') }}</h1>
    <div class="filter-bar">
      <button v-for="category in giftItemData" :key="category.id" :class="{ active: selectedCategory === category.id }" @click="selectCategory(category.id)">
        {{ category.title }}
      </button>
    </div>
    <main class="product-content">
      <section class="product-list-section">
        <div class="product-list">
          <div
            class="product-card"
            v-for="(product, index) in chunkedGiftItem[currentPage - 1]"
            :key="'product-' + index"
            :ref="'product-' + index"
            @click="viewDetails(index)"
            :class="{ active: selectGiftItemIndex === index || oldGiftItemIndex === index }"
          >
            <div class="product-header">
              <div class="profuct-img-box">
                <img :src="product.image" :alt="product.title" class="product-image" @load="onImageLoad" />
                <div class="card-tags promo-tag">
                  <img :src="product.promoTagSrc" alt="" />
                </div>
              </div>
              <h3 class="product-title">{{ product.title }}</h3>
            </div>

            <div class="product-footer">
              <div class="product-prices">
                <span class="original-price" v-if="product.originalPrice"> NT$ {{ product.originalPrice }} </span>
                <span class="current-price">NT$ {{ product.price }}</span>
              </div>
              <div class="card-tags stock-tag" v-if="product.remainingQuantity > 0 && product.remainingQuantity < 10">
                <div class="card-tag" v-html="formattedAddOn_key010(product.remainingQuantity)"></div>
              </div>
              <div v-if="product.remainingQuantity === 0">
                <button class="add-cart-btn button_style_linear btn-disable">{{ $t('product-info.key015') }}</button>
              </div>
              <div v-else>
                <button class="add-cart-btn button_style_linear" @click.stop="addToCart(product)">{{ $t('product-info.key011') }}</button>
              </div>
            </div>
          </div>
          <div class="product-list-empty" v-show="!chunkedGiftItem.length > 0">
            <h1>{{ $t('gift-shop.key004') }}</h1>
          </div>
          <transition name="fade" appear>
            <div v-if="showCartAlert" class="cart-alert">
              <i class="bi bi-cart3"></i>
              <p>{{ $t('product-info.key016') }}</p>
            </div>
          </transition>
        </div>
        <Pagination
          :currentPage="currentPage"
          :totalPages="totalPages"
          :visiblePages="visiblePages"
          :hasMoreItems="hasMoreGiftItem"
          @goToPage="goToPage"
          @updatePage="updatePage"
          v-show="totalPages > 0"
        />
        <div :style="{ height: `${addHeight}px` }" class="add-height" v-if="!isMobile"></div>
      </section>
      <aside class="product-details" v-if="selectedGiftItem && !isMobile">
        <div class="product-header">
          <h3 class="product-details-title">{{ selectedGiftItem.title }}</h3>
          <div class="product-details-content">
            <RichContentRenderer :blocks="selectedGiftItem.description" />
          </div>
        </div>
        <div class="product-footer">
          <div class="product-prices">
            <span class="original-price" v-if="selectedGiftItem.originalPrice"> NT$ {{ selectedGiftItem.originalPrice }} </span>
            <span class="current-price">NT$ {{ selectedGiftItem.price }}</span>
          </div>
          <div v-if="selectedGiftItem.remainingQuantity === 0">
            <button class="add-cart-btn button_style_linear btn-disable">{{ $t('product-info.key015') }}</button>
          </div>
          <div v-else>
            <button class="add-cart-btn button_style_linear" @click="addToCart(selectedGiftItem, 'goCart')">{{ $t('product-info.key010') }}</button>
          </div>
        </div>
      </aside>
      <aside class="product-details product-details-empty" v-else-if="!isMobile">
        <i class="bi bi-box"></i>
        <p>{{ $t('gift-shop.key005') }}</p>
      </aside>
    </main>
  </section>
  <GiftItemMobileDetail
    v-if="selectedGiftItem && isMobile"
    :giftItemDetail="chunkedGiftItem[this.currentPage - 1]"
    :selectGiftItemIndex="selectGiftItemIndex"
    :oldGiftItemIndex="oldGiftItemIndex"
    :currentPage="currentPage"
    :totalPages="totalPages"
    @updatePage="updatePage"
    @close="closeModal"
    @add-to-cart="addToCart"
  />
  <Loading v-show="loading"></Loading>
</template>

<script>
import Loading from '@/components/Loading.vue';
import Pagination from '@/components/Pagination.vue';
import GiftItemMobileDetail from '@/components/giftShop/giftItemMobileDetail.vue';
import RichContentRenderer from '@/components/richContent/RichContentRenderer.vue';
import { chunk } from 'lodash-es';
import { setCartItems, getCartItems, setCartItemExpiry, getCartItemExpiry } from '@/localStorage/cartStorage.js';
import { getECommerceInfo } from '@/api/giftShopService.js';

export default {
  name: 'GiftItem',
  components: {
    Loading,
    Pagination,
    GiftItemMobileDetail,
    RichContentRenderer,
  },
  props: {},
  setup() {
    return {};
  },
  data() {
    return {
      currentPage: 1,
      maxVisiblePages: 5,
      mobileItemsPerPage: 10,
      desktopItemsPerPage: 50,
      // for query next page orders
      lastKeyId: undefined,
      lastKeyCreatedAt: undefined,

      loading: false,
      showCartAlert: false,
      imagesLoaded: 0,
      addHeight: 0,
      // filter
      categories: [],
      giftItemData: [],
      selectedCategory: 'category_1',
      oldGiftItemIndex: null,
      selectGiftItemIndex: null,
    };
  },
  computed: {
    isMobile() {
      return this.$store.state.isMobile;
    },
    // 每頁顯示的項目數量
    itemsPerPage() {
      return this.isMobile ? this.mobileItemsPerPage : this.desktopItemsPerPage;
    },
    chunkedGiftItem() {
      const category = this.giftItemData.find((item) => item.id === this.selectedCategory);
      const filteredItems = category ? category.items : [];
      return chunk(filteredItems, this.itemsPerPage);
    },
    totalPages() {
      return this.chunkedGiftItem.length;
    },
    hasMoreGiftItem() {
      return this.lastKeyId != null;
    },
    visiblePages() {
      if (!this.totalPages) return [];

      let startPage, endPage;

      if (this.totalPages <= this.maxVisiblePages) {
        startPage = 1;
        endPage = this.totalPages;
      } else {
        const middle = Math.floor(this.maxVisiblePages / 2);

        if (this.currentPage <= middle) {
          startPage = 1;
          endPage = this.maxVisiblePages;
        } else if (this.currentPage + middle >= this.totalPages) {
          startPage = this.totalPages - this.maxVisiblePages + 1;
          endPage = this.totalPages;
        } else {
          startPage = this.currentPage - middle;
          endPage = this.currentPage + middle;
        }
      }

      return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
    },
    //選取商品了解詳細內容
    selectedGiftItem() {
      if (this.chunkedGiftItem[this.currentPage - 1] && this.selectGiftItemIndex !== null) {
        return this.chunkedGiftItem[this.currentPage - 1][this.selectGiftItemIndex];
      }
      return null;
    },
  },
  watch: {
    currentPage() {
      $('html, body').animate({ scrollTop: 0 }, 'fast');
    },
    itemsPerPage(newVal, oldVal) {
      const firstOrderIndex = (this.currentPage - 1) * oldVal + 1;
      let newPage = Math.max(1, Math.ceil(firstOrderIndex / newVal));
      this.currentPage = Math.min(newPage, this.totalPages);
    },
  },
  async mounted() {
    this.fetchECommerceInfo();
  },
  methods: {
    async fetchECommerceInfo() {
      try {
        this.loading = true;
        const langSetting = this.currentLanguage === 'zh_tw' ? 'zh-tw' : this.currentLanguage;
        const response = await getECommerceInfo(langSetting);
        const responseData = response.data.data;
        this.addIdsToResponseData(responseData);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    addIdsToResponseData(data) {
      this.giftItemData = data.map((item, index) => ({
        id: `category_${index + 1}`,
        ...item,
      }));
    },
    selectCategory(categoryId) {
      this.selectGiftItemIndex = null;
      this.selectedCategory = categoryId;
      this.currentPage = 1;
    },
    viewDetails(index) {
      this.selectGiftItemIndex = index;
      if (!this.isMobile) {
        const cardRef = this.$refs['product-' + index];
        if (cardRef && cardRef[0]) {
          let offsetTop = cardRef[0].offsetTop - 50;
          window.scrollTo({ top: offsetTop, behavior: 'smooth' });
        }
      } else {
        document.body.style.overflow = 'hidden';
      }
    },
    goToPage(page) {
      if (page > 0 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    updatePage(currentPage) {
      this.currentPage = currentPage;
    },
    closeModal(oldIndex, productId) {
      this.oldGiftItemIndex = oldIndex;
      this.selectGiftItemIndex = null;
      document.body.style.overflow = '';

      const cardRef = this.$refs['product-' + productId];
      if (cardRef && cardRef[0]) {
        let offsetTop = cardRef[0].offsetTop - 60;
        window.scrollTo({ top: offsetTop, behavior: 'smooth' });
      }
    },
    addCart() {
      this.closeModal();
      this.showCartAlert = true;
      setTimeout(() => {
        this.showCartAlert = false;
      }, 1000);
    },
    addToCart(product, type) {
      // 取得 localStorage 內的購物車資料
      const cartItems = getCartItems() || [];
      const expiry = getCartItemExpiry() || {};

      // 定義當前商品
      const newItem = {
        productId: product.productId,
        sessionId: product.sessionId,
        packId: product.packId,
        quantity: 1,
      };

      const existingIndex = cartItems.findIndex((item) => item.productId === newItem.productId && item.sessionId === newItem.sessionId && item.packId === newItem.packId);

      if (existingIndex !== -1) {
        cartItems[existingIndex].quantity += 1;
      } else {
        cartItems.push(newItem);
      }

      //更新 localStorage
      setCartItems(cartItems);

      const itemKey = `${newItem.productId}-${newItem.sessionId}`;
      const EXPIRY_DURATION = 24 * 60 * 60 * 1000;
      const now = new Date().getTime();
      expiry[itemKey] = now + EXPIRY_DURATION;
      setCartItemExpiry(expiry);

      //是否跳轉到購物車
      if (type === 'goCart') {
        this.$router.push(`/${this.currentLanguage}/cart`);
      } else {
        this.closeModal();
        this.showCartAlert = true;
        setTimeout(() => {
          this.showCartAlert = false;
        }, 1000);
      }
    },
    //剩最後 { quantity } 組
    formattedAddOn_key010(itemQuantity) {
      return this.$t('shopping-cart.addOn_key010', {
        quantity: `<span class='current_price'> ${itemQuantity} </span>`,
      });
    },
    //增加高度
    calculateAddHeight() {
      this.$nextTick(() => {
        if (!this.isMobile) {
          let viewportHeight = Math.floor(window.innerHeight * 0.65);
          let lastCardHeight = Math.floor($('.product-card').last().innerHeight());
          this.addHeight = Math.max(viewportHeight - lastCardHeight, 0);
        } else {
          this.addHeight = 0;
        }
      });
    },
    onImageLoad() {
      this.imagesLoaded++;
      if (this.imagesLoaded === this.giftItemData.length) {
        this.calculateAddHeight();
      }
    },
  },
};
</script>

<style lang="scss"></style>
